import { FC, useState, useCallback, useEffect } from 'react';
import { Stack, Typography, Box, Button } from '@mui/material';
import DashboardAccessItem from './DashboardAccessItem';
import { Dashboard, DashboardStatus } from '@api/types/SelfServeDetails';
import { DashboardAccessItemStatus } from './types';
import { DataAccessProvider, SelfServeDataAccessStatus } from '@constants/constants';

interface UserDashboardsSectionProps {
  dashboards: Dashboard[];
  mmp: string;
  requestStatus?: string;
  onPublish: (selectedDashboards: string[]) => void;
}

const UserDashboardStatusMap: Record<DashboardStatus, DashboardAccessItemStatus> = {
  PUBLISHED: 'GRANTED',
  PENDING: 'PENDING',
  REQUESTED: 'PENDING',
  READY_TO_PREPROCESS: 'PENDING',
  READY_TO_PROCESS: 'PENDING',
  PENDING_DATA_PULL: 'PENDING',
  DATA_PULL_INITIATED: 'PENDING',
  READY_TO_PUBLISH: 'PENDING',
  FAILED: 'FAILED',
  DATA_FETCH_FAILED: 'FAILED',
  REJECTED: 'FAILED',
  NOT_REQUESTED: 'DISABLED',
};

// Helper function to determine if a dashboard should be checked by default
const shouldBeCheckedByDefault = (status: DashboardStatus): boolean => {
  return status === 'PUBLISHED';
};

// Helper function to determine if a dashboard checkbox should be disabled
const shouldBeDisabled = (status: DashboardStatus): boolean => {
  return status === 'PUBLISHED';
};

const UserDashboardsSection: FC<UserDashboardsSectionProps> = ({
  dashboards = [],
  mmp = '',
  requestStatus = '',
  onPublish,
}) => {
  const showCheckboxes =
    mmp !== DataAccessProvider.APPS_FLYER &&
    mmp !== DataAccessProvider.ADJUST &&
    requestStatus !== SelfServeDataAccessStatus.PUBLISHED;

  // Initialize state with pre-checked items based on their status
  const [checkedDashboards, setCheckedDashboards] = useState<string[]>([]);

  // Initialize checked state based on dashboard status
  useEffect(() => {
    if (dashboards.length > 0) {
      const initialCheckedDashboards = dashboards
        .filter((dashboard) => shouldBeCheckedByDefault(dashboard.status))
        .map((dashboard) => dashboard.id);

      setCheckedDashboards(initialCheckedDashboards);
    }
  }, [dashboards]);

  // Handler for dashboard checkbox changes
  const handleDashboardCheck = useCallback((id: string, checked: boolean) => {
    setCheckedDashboards((prev) =>
      checked ? [...prev, id] : prev.filter((dashboardId) => dashboardId !== id),
    );
  }, []);

  // Handle publish button click
  const handlePublish = () => {
    onPublish(checkedDashboards);
  };

  return (
    <Stack gap={'1.5rem'}>
      {/* User Dashboards Section */}
      <Stack
        gap={'1rem'}
        sx={{
          background: '#fff',
          borderRadius: '1rem',
          p: '1rem',
        }}
      >
        <Stack gap={'1rem'}>
          <Typography fontSize={'1rem'} lineHeight={'1.25rem'} fontWeight={700} color={'#6C6C6C'}>
            User Dashboards
          </Typography>

          {dashboards.length > 0 ? (
            dashboards.map((dashboard) => (
              <DashboardAccessItem
                key={dashboard.id}
                id={dashboard.id}
                name={dashboard.name}
                status={UserDashboardStatusMap[dashboard.status]}
                icon={dashboard.name}
                checked={checkedDashboards.includes(dashboard.id)}
                disabled={shouldBeDisabled(dashboard.status)}
                showCheckbox={showCheckboxes}
                onCheckChange={handleDashboardCheck}
              />
            ))
          ) : (
            <Box
              sx={{
                border: '1px solid #E8EAEB',
                padding: '0.75rem 1.125rem',
                borderRadius: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                backgroundColor: '#F0F0F1',
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Manrope, sans-serif',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  color: '#162936',
                }}
              >
                No Dashboards available
              </Typography>
            </Box>
          )}
        </Stack>

        <Stack gap={'1rem'}>
          {showCheckboxes && (
            <Box>
              <Button
                disableElevation
                disableFocusRipple
                disableRipple
                disableTouchRipple
                variant="contained"
                color="primary"
                onClick={handlePublish}
                disabled={checkedDashboards.length === 0}
                sx={{
                  borderRadius: '1.25rem',
                  textTransform: 'none',
                  background: '#2F736E',
                  color: '#fff',
                  p: '0.5rem 1rem',
                }}
              >
                Publish
              </Button>
            </Box>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserDashboardsSection;
